import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, inject, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { AuthService } from '@ortho-next/auth-core';
import { firstValueFrom, map, of, switchMap } from 'rxjs';
import { routes } from './app.routes';
import { initAuth } from './auth.config';
import { APP_CONFIG, getLocale, HttpErrorInterceptor, initializeAppConfig, initLocale, LanguageService, MonitoringErrorHandler } from './core';
import { UserService } from './services';


export async function initApp(authSrv: AuthService, usrSrv: UserService, langSrv: LanguageService) {
	return firstValueFrom(authSrv.isDoneLoading$.pipe(
		map(() => !!authSrv.accessToken),
		switchMap(isAuth => isAuth ? usrSrv.loadUser() : of(null)),
		switchMap(usr => usr ? langSrv.loadLabels(usr.language) : of(null))
	));
}

export const appConfig: ApplicationConfig = {
	providers: [
		provideZoneChangeDetection({ eventCoalescing: true }),
		provideRouter(routes),
		provideHttpClient(),
		provideAppInitializer(() => initLocale(inject(LanguageService), inject(Router))),
		provideAppInitializer(() => initAuth(inject(AuthService))),
		provideAppInitializer(() => initApp(inject(AuthService), inject(UserService), inject(LanguageService))),
		provideAppInitializer(() => initializeAppConfig(inject(APP_CONFIG))),
		{ provide: LOCALE_ID, useFactory: getLocale, deps: [LanguageService] },
		{ provide: ErrorHandler, useClass: MonitoringErrorHandler },
		{ provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true }
	]
};

import '@angular/common/locales/global/de';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/it';


import '@angular/common/locales/global/af';
import '@angular/common/locales/global/af-NA';
import '@angular/common/locales/global/agq';
import '@angular/common/locales/global/ak';
import '@angular/common/locales/global/am';
import '@angular/common/locales/global/ar';
import '@angular/common/locales/global/ar-AE';
import '@angular/common/locales/global/ar-BH';
import '@angular/common/locales/global/ar-DJ';
import '@angular/common/locales/global/ar-DZ';
import '@angular/common/locales/global/ar-EG';
import '@angular/common/locales/global/ar-EH';
import '@angular/common/locales/global/ar-ER';
import '@angular/common/locales/global/ar-IL';
import '@angular/common/locales/global/ar-IQ';
import '@angular/common/locales/global/ar-JO';
import '@angular/common/locales/global/ar-KM';
import '@angular/common/locales/global/ar-KW';
import '@angular/common/locales/global/ar-LB';
import '@angular/common/locales/global/ar-LY';
import '@angular/common/locales/global/ar-MA';
import '@angular/common/locales/global/ar-MR';
import '@angular/common/locales/global/ar-OM';
import '@angular/common/locales/global/ar-PS';
import '@angular/common/locales/global/ar-QA';
import '@angular/common/locales/global/ar-SA';
import '@angular/common/locales/global/ar-SD';
import '@angular/common/locales/global/ar-SO';
import '@angular/common/locales/global/ar-SS';
import '@angular/common/locales/global/ar-SY';
import '@angular/common/locales/global/ar-TD';
import '@angular/common/locales/global/ar-TN';
import '@angular/common/locales/global/ar-YE';
import '@angular/common/locales/global/as';
import '@angular/common/locales/global/asa';
import '@angular/common/locales/global/ast';
import '@angular/common/locales/global/az';
import '@angular/common/locales/global/az-Cyrl';
import '@angular/common/locales/global/az-Latn';
import '@angular/common/locales/global/bas';
import '@angular/common/locales/global/be';
import '@angular/common/locales/global/be-tarask';
import '@angular/common/locales/global/bem';
import '@angular/common/locales/global/bez';
import '@angular/common/locales/global/bg';
import '@angular/common/locales/global/bm';
import '@angular/common/locales/global/bn';
import '@angular/common/locales/global/bn-IN';
import '@angular/common/locales/global/bo';
import '@angular/common/locales/global/bo-IN';
import '@angular/common/locales/global/br';
import '@angular/common/locales/global/brx';
import '@angular/common/locales/global/bs';
import '@angular/common/locales/global/bs-Cyrl';
import '@angular/common/locales/global/bs-Latn';
import '@angular/common/locales/global/ca';
import '@angular/common/locales/global/ca-AD';
import '@angular/common/locales/global/ca-ES-valencia';
import '@angular/common/locales/global/ca-FR';
import '@angular/common/locales/global/ca-IT';
import '@angular/common/locales/global/ccp';
import '@angular/common/locales/global/ccp-IN';
import '@angular/common/locales/global/ce';
import '@angular/common/locales/global/ceb';
import '@angular/common/locales/global/cgg';
import '@angular/common/locales/global/chr';
import '@angular/common/locales/global/ckb';
import '@angular/common/locales/global/ckb-IR';
import '@angular/common/locales/global/cs';
import '@angular/common/locales/global/cy';
import '@angular/common/locales/global/da';
import '@angular/common/locales/global/da-GL';
import '@angular/common/locales/global/dav';

import '@angular/common/locales/global/de-AT';
import '@angular/common/locales/global/de-BE';
import '@angular/common/locales/global/de-CH';
import '@angular/common/locales/global/de-IT';
import '@angular/common/locales/global/de-LI';
import '@angular/common/locales/global/de-LU';
import '@angular/common/locales/global/dje';
import '@angular/common/locales/global/doi';
import '@angular/common/locales/global/dsb';
import '@angular/common/locales/global/dua';
import '@angular/common/locales/global/dyo';
import '@angular/common/locales/global/dz';
import '@angular/common/locales/global/ebu';
import '@angular/common/locales/global/ee';
import '@angular/common/locales/global/ee-TG';
import '@angular/common/locales/global/el';
import '@angular/common/locales/global/el-CY';

import '@angular/common/locales/global/en-001';
import '@angular/common/locales/global/en-150';
import '@angular/common/locales/global/en-AE';
import '@angular/common/locales/global/en-AG';
import '@angular/common/locales/global/en-AI';
import '@angular/common/locales/global/en-AS';
import '@angular/common/locales/global/en-AT';
import '@angular/common/locales/global/en-AU';
import '@angular/common/locales/global/en-BB';
import '@angular/common/locales/global/en-BE';
import '@angular/common/locales/global/en-BI';
import '@angular/common/locales/global/en-BM';
import '@angular/common/locales/global/en-BS';
import '@angular/common/locales/global/en-BW';
import '@angular/common/locales/global/en-BZ';
import '@angular/common/locales/global/en-CA';
import '@angular/common/locales/global/en-CC';
import '@angular/common/locales/global/en-CH';
import '@angular/common/locales/global/en-CK';
import '@angular/common/locales/global/en-CM';
import '@angular/common/locales/global/en-CX';
import '@angular/common/locales/global/en-CY';
import '@angular/common/locales/global/en-DE';
import '@angular/common/locales/global/en-DG';
import '@angular/common/locales/global/en-DK';
import '@angular/common/locales/global/en-DM';
import '@angular/common/locales/global/en-ER';
import '@angular/common/locales/global/en-FI';
import '@angular/common/locales/global/en-FJ';
import '@angular/common/locales/global/en-FK';
import '@angular/common/locales/global/en-FM';
import '@angular/common/locales/global/en-GB';
import '@angular/common/locales/global/en-GD';
import '@angular/common/locales/global/en-GG';
import '@angular/common/locales/global/en-GH';
import '@angular/common/locales/global/en-GI';
import '@angular/common/locales/global/en-GM';
import '@angular/common/locales/global/en-GU';
import '@angular/common/locales/global/en-GY';
import '@angular/common/locales/global/en-HK';
import '@angular/common/locales/global/en-IE';
import '@angular/common/locales/global/en-IL';
import '@angular/common/locales/global/en-IM';
import '@angular/common/locales/global/en-IN';
import '@angular/common/locales/global/en-IO';
import '@angular/common/locales/global/en-JE';
import '@angular/common/locales/global/en-JM';
import '@angular/common/locales/global/en-KE';
import '@angular/common/locales/global/en-KI';
import '@angular/common/locales/global/en-KN';
import '@angular/common/locales/global/en-KY';
import '@angular/common/locales/global/en-LC';
import '@angular/common/locales/global/en-LR';
import '@angular/common/locales/global/en-LS';
import '@angular/common/locales/global/en-MG';
import '@angular/common/locales/global/en-MH';
import '@angular/common/locales/global/en-MO';
import '@angular/common/locales/global/en-MP';
import '@angular/common/locales/global/en-MS';
import '@angular/common/locales/global/en-MT';
import '@angular/common/locales/global/en-MU';
import '@angular/common/locales/global/en-MV';
import '@angular/common/locales/global/en-MW';
import '@angular/common/locales/global/en-MY';
import '@angular/common/locales/global/en-NA';
import '@angular/common/locales/global/en-NF';
import '@angular/common/locales/global/en-NG';
import '@angular/common/locales/global/en-NL';
import '@angular/common/locales/global/en-NR';
import '@angular/common/locales/global/en-NU';
import '@angular/common/locales/global/en-NZ';
import '@angular/common/locales/global/en-PG';
import '@angular/common/locales/global/en-PH';
import '@angular/common/locales/global/en-PK';
import '@angular/common/locales/global/en-PN';
import '@angular/common/locales/global/en-PR';
import '@angular/common/locales/global/en-PW';
import '@angular/common/locales/global/en-RW';
import '@angular/common/locales/global/en-SB';
import '@angular/common/locales/global/en-SC';
import '@angular/common/locales/global/en-SD';
import '@angular/common/locales/global/en-SE';
import '@angular/common/locales/global/en-SG';
import '@angular/common/locales/global/en-SH';
import '@angular/common/locales/global/en-SI';
import '@angular/common/locales/global/en-SL';
import '@angular/common/locales/global/en-SS';
import '@angular/common/locales/global/en-SX';
import '@angular/common/locales/global/en-SZ';
import '@angular/common/locales/global/en-TC';
import '@angular/common/locales/global/en-TK';
import '@angular/common/locales/global/en-TO';
import '@angular/common/locales/global/en-TT';
import '@angular/common/locales/global/en-TV';
import '@angular/common/locales/global/en-TZ';
import '@angular/common/locales/global/en-UG';
import '@angular/common/locales/global/en-UM';
import '@angular/common/locales/global/en-VC';
import '@angular/common/locales/global/en-VG';
import '@angular/common/locales/global/en-VI';
import '@angular/common/locales/global/en-VU';
import '@angular/common/locales/global/en-WS';
import '@angular/common/locales/global/en-ZA';
import '@angular/common/locales/global/en-ZM';
import '@angular/common/locales/global/en-ZW';
import '@angular/common/locales/global/eo';
import '@angular/common/locales/global/es';
import '@angular/common/locales/global/es-419';
import '@angular/common/locales/global/es-AR';
import '@angular/common/locales/global/es-BO';
import '@angular/common/locales/global/es-BR';
import '@angular/common/locales/global/es-BZ';
import '@angular/common/locales/global/es-CL';
import '@angular/common/locales/global/es-CO';
import '@angular/common/locales/global/es-CR';
import '@angular/common/locales/global/es-CU';
import '@angular/common/locales/global/es-DO';
import '@angular/common/locales/global/es-EA';
import '@angular/common/locales/global/es-EC';
import '@angular/common/locales/global/es-GQ';
import '@angular/common/locales/global/es-GT';
import '@angular/common/locales/global/es-HN';
import '@angular/common/locales/global/es-IC';
import '@angular/common/locales/global/es-MX';
import '@angular/common/locales/global/es-NI';
import '@angular/common/locales/global/es-PA';
import '@angular/common/locales/global/es-PE';
import '@angular/common/locales/global/es-PH';
import '@angular/common/locales/global/es-PR';
import '@angular/common/locales/global/es-PY';
import '@angular/common/locales/global/es-SV';
import '@angular/common/locales/global/es-US';
import '@angular/common/locales/global/es-UY';
import '@angular/common/locales/global/es-VE';
import '@angular/common/locales/global/et';
import '@angular/common/locales/global/eu';
import '@angular/common/locales/global/ewo';
import '@angular/common/locales/global/fa';
import '@angular/common/locales/global/fa-AF';
import '@angular/common/locales/global/ff';
import '@angular/common/locales/global/ff-Adlm';
import '@angular/common/locales/global/ff-Adlm-BF';
import '@angular/common/locales/global/ff-Adlm-CM';
import '@angular/common/locales/global/ff-Adlm-GH';
import '@angular/common/locales/global/ff-Adlm-GM';
import '@angular/common/locales/global/ff-Adlm-GW';
import '@angular/common/locales/global/ff-Adlm-LR';
import '@angular/common/locales/global/ff-Adlm-MR';
import '@angular/common/locales/global/ff-Adlm-NE';
import '@angular/common/locales/global/ff-Adlm-NG';
import '@angular/common/locales/global/ff-Adlm-SL';
import '@angular/common/locales/global/ff-Adlm-SN';
import '@angular/common/locales/global/ff-Latn';
import '@angular/common/locales/global/ff-Latn-BF';
import '@angular/common/locales/global/ff-Latn-CM';
import '@angular/common/locales/global/ff-Latn-GH';
import '@angular/common/locales/global/ff-Latn-GM';
import '@angular/common/locales/global/ff-Latn-GN';
import '@angular/common/locales/global/ff-Latn-GW';
import '@angular/common/locales/global/ff-Latn-LR';
import '@angular/common/locales/global/ff-Latn-MR';
import '@angular/common/locales/global/ff-Latn-NE';
import '@angular/common/locales/global/ff-Latn-NG';
import '@angular/common/locales/global/ff-Latn-SL';
import '@angular/common/locales/global/fi';
import '@angular/common/locales/global/fil';
import '@angular/common/locales/global/fo';
import '@angular/common/locales/global/fo-DK';
import '@angular/common/locales/global/fr';
import '@angular/common/locales/global/fr-BE';
import '@angular/common/locales/global/fr-BF';
import '@angular/common/locales/global/fr-BI';
import '@angular/common/locales/global/fr-BJ';
import '@angular/common/locales/global/fr-BL';
import '@angular/common/locales/global/fr-CA';
import '@angular/common/locales/global/fr-CD';
import '@angular/common/locales/global/fr-CF';
import '@angular/common/locales/global/fr-CG';
import '@angular/common/locales/global/fr-CH';
import '@angular/common/locales/global/fr-CI';
import '@angular/common/locales/global/fr-CM';
import '@angular/common/locales/global/fr-DJ';
import '@angular/common/locales/global/fr-DZ';
import '@angular/common/locales/global/fr-GA';
import '@angular/common/locales/global/fr-GF';
import '@angular/common/locales/global/fr-GN';
import '@angular/common/locales/global/fr-GP';
import '@angular/common/locales/global/fr-GQ';
import '@angular/common/locales/global/fr-HT';
import '@angular/common/locales/global/fr-KM';
import '@angular/common/locales/global/fr-LU';
import '@angular/common/locales/global/fr-MA';
import '@angular/common/locales/global/fr-MC';
import '@angular/common/locales/global/fr-MF';
import '@angular/common/locales/global/fr-MG';
import '@angular/common/locales/global/fr-ML';
import '@angular/common/locales/global/fr-MQ';
import '@angular/common/locales/global/fr-MR';
import '@angular/common/locales/global/fr-MU';
import '@angular/common/locales/global/fr-NC';
import '@angular/common/locales/global/fr-NE';
import '@angular/common/locales/global/fr-PF';
import '@angular/common/locales/global/fr-PM';
import '@angular/common/locales/global/fr-RE';
import '@angular/common/locales/global/fr-RW';
import '@angular/common/locales/global/fr-SC';
import '@angular/common/locales/global/fr-SN';
import '@angular/common/locales/global/fr-SY';
import '@angular/common/locales/global/fr-TD';
import '@angular/common/locales/global/fr-TG';
import '@angular/common/locales/global/fr-TN';
import '@angular/common/locales/global/fr-VU';
import '@angular/common/locales/global/fr-WF';
import '@angular/common/locales/global/fr-YT';
import '@angular/common/locales/global/fur';
import '@angular/common/locales/global/fy';
import '@angular/common/locales/global/ga';
import '@angular/common/locales/global/ga-GB';
import '@angular/common/locales/global/gd';
import '@angular/common/locales/global/gl';
import '@angular/common/locales/global/gsw';
import '@angular/common/locales/global/gsw-FR';
import '@angular/common/locales/global/gsw-LI';
import '@angular/common/locales/global/gu';
import '@angular/common/locales/global/guz';
import '@angular/common/locales/global/gv';
import '@angular/common/locales/global/ha';
import '@angular/common/locales/global/ha-GH';
import '@angular/common/locales/global/ha-NE';
import '@angular/common/locales/global/haw';
import '@angular/common/locales/global/he';
import '@angular/common/locales/global/hi';
import '@angular/common/locales/global/hi-Latn';
import '@angular/common/locales/global/hr';
import '@angular/common/locales/global/hr-BA';
import '@angular/common/locales/global/hsb';
import '@angular/common/locales/global/hu';
import '@angular/common/locales/global/hy';
import '@angular/common/locales/global/ia';
import '@angular/common/locales/global/id';
import '@angular/common/locales/global/ig';
import '@angular/common/locales/global/ii';
import '@angular/common/locales/global/is';
import '@angular/common/locales/global/it-CH';
import '@angular/common/locales/global/it-SM';
import '@angular/common/locales/global/it-VA';
import '@angular/common/locales/global/ja';
import '@angular/common/locales/global/jgo';
import '@angular/common/locales/global/jmc';
import '@angular/common/locales/global/jv';
import '@angular/common/locales/global/ka';
import '@angular/common/locales/global/kab';
import '@angular/common/locales/global/kam';
import '@angular/common/locales/global/kde';
import '@angular/common/locales/global/kea';
import '@angular/common/locales/global/kgp';
import '@angular/common/locales/global/khq';
import '@angular/common/locales/global/ki';
import '@angular/common/locales/global/kk';
import '@angular/common/locales/global/kkj';
import '@angular/common/locales/global/kl';
import '@angular/common/locales/global/kln';
import '@angular/common/locales/global/km';
import '@angular/common/locales/global/kn';
import '@angular/common/locales/global/ko';
import '@angular/common/locales/global/ko-KP';
import '@angular/common/locales/global/kok';
import '@angular/common/locales/global/ks';
import '@angular/common/locales/global/ks-Arab';
import '@angular/common/locales/global/ks-Deva';
import '@angular/common/locales/global/ksb';
import '@angular/common/locales/global/ksf';
import '@angular/common/locales/global/ksh';
import '@angular/common/locales/global/ku';
import '@angular/common/locales/global/kw';
import '@angular/common/locales/global/ky';
import '@angular/common/locales/global/lag';
import '@angular/common/locales/global/lb';
import '@angular/common/locales/global/lg';
import '@angular/common/locales/global/lkt';
import '@angular/common/locales/global/ln';
import '@angular/common/locales/global/ln-AO';
import '@angular/common/locales/global/ln-CF';
import '@angular/common/locales/global/ln-CG';
import '@angular/common/locales/global/lo';
import '@angular/common/locales/global/lrc';
import '@angular/common/locales/global/lrc-IQ';
import '@angular/common/locales/global/lt';
import '@angular/common/locales/global/lu';
import '@angular/common/locales/global/luo';
import '@angular/common/locales/global/luy';
import '@angular/common/locales/global/lv';
import '@angular/common/locales/global/mai';
import '@angular/common/locales/global/mas';
import '@angular/common/locales/global/mas-TZ';
import '@angular/common/locales/global/mer';
import '@angular/common/locales/global/mfe';
import '@angular/common/locales/global/mg';
import '@angular/common/locales/global/mgh';
import '@angular/common/locales/global/mgo';
import '@angular/common/locales/global/mi';
import '@angular/common/locales/global/mk';
import '@angular/common/locales/global/ml';
import '@angular/common/locales/global/mn';
import '@angular/common/locales/global/mni';
import '@angular/common/locales/global/mni-Beng';
import '@angular/common/locales/global/mr';
import '@angular/common/locales/global/ms';
import '@angular/common/locales/global/ms-BN';
import '@angular/common/locales/global/ms-ID';
import '@angular/common/locales/global/ms-SG';
import '@angular/common/locales/global/mt';
import '@angular/common/locales/global/mua';
import '@angular/common/locales/global/my';
import '@angular/common/locales/global/mzn';
import '@angular/common/locales/global/naq';
import '@angular/common/locales/global/nb';
import '@angular/common/locales/global/nb-SJ';
import '@angular/common/locales/global/nd';
import '@angular/common/locales/global/nds';
import '@angular/common/locales/global/nds-NL';
import '@angular/common/locales/global/ne';
import '@angular/common/locales/global/ne-IN';
import '@angular/common/locales/global/nl';
import '@angular/common/locales/global/nl-AW';
import '@angular/common/locales/global/nl-BE';
import '@angular/common/locales/global/nl-BQ';
import '@angular/common/locales/global/nl-CW';
import '@angular/common/locales/global/nl-SR';
import '@angular/common/locales/global/nl-SX';
import '@angular/common/locales/global/nmg';
import '@angular/common/locales/global/nn';
import '@angular/common/locales/global/nnh';
import '@angular/common/locales/global/no';
import '@angular/common/locales/global/nus';
import '@angular/common/locales/global/nyn';
import '@angular/common/locales/global/om';
import '@angular/common/locales/global/om-KE';
import '@angular/common/locales/global/or';
import '@angular/common/locales/global/os';
import '@angular/common/locales/global/os-RU';
import '@angular/common/locales/global/pa';
import '@angular/common/locales/global/pa-Arab';
import '@angular/common/locales/global/pa-Guru';
import '@angular/common/locales/global/pcm';
import '@angular/common/locales/global/pl';
import '@angular/common/locales/global/ps';
import '@angular/common/locales/global/ps-PK';
import '@angular/common/locales/global/pt';
import '@angular/common/locales/global/pt-AO';
import '@angular/common/locales/global/pt-CH';
import '@angular/common/locales/global/pt-CV';
import '@angular/common/locales/global/pt-GQ';
import '@angular/common/locales/global/pt-GW';
import '@angular/common/locales/global/pt-LU';
import '@angular/common/locales/global/pt-MO';
import '@angular/common/locales/global/pt-MZ';
import '@angular/common/locales/global/pt-PT';
import '@angular/common/locales/global/pt-ST';
import '@angular/common/locales/global/pt-TL';
import '@angular/common/locales/global/qu';
import '@angular/common/locales/global/qu-BO';
import '@angular/common/locales/global/qu-EC';
import '@angular/common/locales/global/rm';
import '@angular/common/locales/global/rn';
import '@angular/common/locales/global/ro';
import '@angular/common/locales/global/ro-MD';
import '@angular/common/locales/global/rof';
import '@angular/common/locales/global/ru';
import '@angular/common/locales/global/ru-BY';
import '@angular/common/locales/global/ru-KG';
import '@angular/common/locales/global/ru-KZ';
import '@angular/common/locales/global/ru-MD';
import '@angular/common/locales/global/ru-UA';
import '@angular/common/locales/global/rw';
import '@angular/common/locales/global/rwk';
import '@angular/common/locales/global/sa';
import '@angular/common/locales/global/sah';
import '@angular/common/locales/global/saq';
import '@angular/common/locales/global/sat';
import '@angular/common/locales/global/sat-Olck';
import '@angular/common/locales/global/sbp';
import '@angular/common/locales/global/sc';
import '@angular/common/locales/global/sd';
import '@angular/common/locales/global/sd-Arab';
import '@angular/common/locales/global/sd-Deva';
import '@angular/common/locales/global/se';
import '@angular/common/locales/global/se-FI';
import '@angular/common/locales/global/se-SE';
import '@angular/common/locales/global/seh';
import '@angular/common/locales/global/ses';
import '@angular/common/locales/global/sg';
import '@angular/common/locales/global/shi';
import '@angular/common/locales/global/shi-Latn';
import '@angular/common/locales/global/shi-Tfng';
import '@angular/common/locales/global/si';
import '@angular/common/locales/global/sk';
import '@angular/common/locales/global/sl';
import '@angular/common/locales/global/smn';
import '@angular/common/locales/global/sn';
import '@angular/common/locales/global/so';
import '@angular/common/locales/global/so-DJ';
import '@angular/common/locales/global/so-ET';
import '@angular/common/locales/global/so-KE';
import '@angular/common/locales/global/sq';
import '@angular/common/locales/global/sq-MK';
import '@angular/common/locales/global/sq-XK';
import '@angular/common/locales/global/sr';
import '@angular/common/locales/global/sr-Cyrl';
import '@angular/common/locales/global/sr-Cyrl-BA';
import '@angular/common/locales/global/sr-Cyrl-ME';
import '@angular/common/locales/global/sr-Cyrl-XK';
import '@angular/common/locales/global/sr-Latn';
import '@angular/common/locales/global/sr-Latn-BA';
import '@angular/common/locales/global/sr-Latn-ME';
import '@angular/common/locales/global/sr-Latn-XK';
import '@angular/common/locales/global/su';
import '@angular/common/locales/global/su-Latn';
import '@angular/common/locales/global/sv';
import '@angular/common/locales/global/sv-AX';
import '@angular/common/locales/global/sv-FI';
import '@angular/common/locales/global/sw';
import '@angular/common/locales/global/sw-CD';
import '@angular/common/locales/global/sw-KE';
import '@angular/common/locales/global/sw-UG';
import '@angular/common/locales/global/ta';
import '@angular/common/locales/global/ta-LK';
import '@angular/common/locales/global/ta-MY';
import '@angular/common/locales/global/ta-SG';
import '@angular/common/locales/global/te';
import '@angular/common/locales/global/teo';
import '@angular/common/locales/global/teo-KE';
import '@angular/common/locales/global/tg';
import '@angular/common/locales/global/th';
import '@angular/common/locales/global/ti';
import '@angular/common/locales/global/ti-ER';
import '@angular/common/locales/global/tk';
import '@angular/common/locales/global/to';
import '@angular/common/locales/global/tr';
import '@angular/common/locales/global/tr-CY';
import '@angular/common/locales/global/tt';
import '@angular/common/locales/global/twq';
import '@angular/common/locales/global/tzm';
import '@angular/common/locales/global/ug';
import '@angular/common/locales/global/uk';
import '@angular/common/locales/global/und';
import '@angular/common/locales/global/ur';
import '@angular/common/locales/global/ur-IN';
import '@angular/common/locales/global/uz';
import '@angular/common/locales/global/uz-Arab';
import '@angular/common/locales/global/uz-Cyrl';
import '@angular/common/locales/global/uz-Latn';
import '@angular/common/locales/global/vai';
import '@angular/common/locales/global/vai-Latn';
import '@angular/common/locales/global/vai-Vaii';
import '@angular/common/locales/global/vi';
import '@angular/common/locales/global/vun';
import '@angular/common/locales/global/wae';
import '@angular/common/locales/global/wo';
import '@angular/common/locales/global/xh';
import '@angular/common/locales/global/xog';
import '@angular/common/locales/global/yav';
import '@angular/common/locales/global/yi';
import '@angular/common/locales/global/yo';
import '@angular/common/locales/global/yo-BJ';
import '@angular/common/locales/global/yrl';
import '@angular/common/locales/global/yrl-CO';
import '@angular/common/locales/global/yrl-VE';
import '@angular/common/locales/global/yue';
import '@angular/common/locales/global/yue-Hans';
import '@angular/common/locales/global/yue-Hant';
import '@angular/common/locales/global/zgh';
import '@angular/common/locales/global/zh';
import '@angular/common/locales/global/zh-Hans';
import '@angular/common/locales/global/zh-Hans-HK';
import '@angular/common/locales/global/zh-Hans-MO';
import '@angular/common/locales/global/zh-Hans-SG';
import '@angular/common/locales/global/zh-Hant';
import '@angular/common/locales/global/zh-Hant-HK';
import '@angular/common/locales/global/zh-Hant-MO';
import '@angular/common/locales/global/zu';